import { RefObject, useEffect, useState } from "react";
import styled from "styled-components";
import { Modal } from "react-bootstrap";

import YouTubePlayModal from "./YoutubePlayModal";
import { YoutubeContentApiResponse } from "../../../api/youtubeContentApi";
import { CategoryApiResponse } from "../../../api/categoryApi";
import { SubCategoryApiResponse } from "../../../api/subCategoryApi";
import { getScreenWidth } from "../../../constants";
import AffiliateModal from "../AffiliateModal";
import { AffiliateApiResponse, fetchAffiliate } from "../../../api/affiliateApi";
import AdComponent from "../AdComponent";

type ScreenWidthProps = {
  $isDesktop?: boolean;
  $isMobile?: boolean;
  $isTwoColumn?: boolean;
};

type IsScrollingProps = {
  $isScrolling: boolean;
};

const H2Title = styled.h2<IsScrollingProps>`
  position: ${(props) => (props.$isScrolling ? "" : "-webkit-sticky")};
  position: ${(props) => (props.$isScrolling ? "" : "sticky")};
  top: 80px;
  height: 60px;
  padding: 10px;
  margin: 0 0 0 15px;
  font-size: 1.7em;
  font-weight: bold;
  scroll-margin-top: 80px;

  &::before {
    content: "";
    display: block;
    position: absolute;
    top: -10px;
    left: 0;
    right: 0;
    height: 10px;
    background-color: white;
  }

  text-shadow:
    -1px -1px 0 #fff,
    1px -1px 0 #fff,
    -1px 1px 0 #fff,
    1px 1px 0 #fff;
`;

const H3Title = styled.h3`
  padding: 5px;
  margin: 20px 0 0 40px;
  font-size: 1.5em;
  font-weight: bold;
  scroll-margin-top: 160px;
`;

const H4Title = styled.h4`
  padding: 5px 0;
  font-size: 1.2em;
  font-weight: bold;
`;

const Container = styled.div<ScreenWidthProps>`
  display: ${(props) => (props.$isTwoColumn ? "flex" : "block")};
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 0 auto;
`;

const Block = styled.div`
  display: flex;
  justify-content: center;
  flex: 0 0 50%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  box-shadow: 3px 3px 3px 3px #888888;
  max-width: 500px;
  max-height: 420px;
  padding: 15px;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const StyledModal = styled(Modal)<ScreenWidthProps>`
  .modal-dialog {
    max-width: ${(props) => (props.$isDesktop ? "1140px" : props.$isMobile ? "100%" : "90%")};
  }
`;

const SummaryBlockStyle = styled.div`
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const MainContent = ({
  youtubeContents,
  categories,
  subCategories,
  isScrolling,
  refs,
}: {
  youtubeContents: YoutubeContentApiResponse[];
  categories: CategoryApiResponse[];
  subCategories: SubCategoryApiResponse[];
  isScrolling: boolean;
  refs: Record<string, RefObject<HTMLDivElement>>;
}) => {
  const { isMobile, isDesktop, isTwoColumn } = getScreenWidth();

  const [isShowYotubePlayModal, setIsShowYotubePlayModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState<YoutubeContentApiResponse | null>(null);
  const [isShowAffiliateModal, setIsShowAffiliateModal] = useState(false);
  const [affiliates, setAffiliates] = useState<AffiliateApiResponse[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const affiliatesData = await fetchAffiliate();
        setAffiliates(affiliatesData);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const handleItemClick = (content: YoutubeContentApiResponse) => {
    setSelectedContent(content);
    setIsShowYotubePlayModal(true);
  };

  const closeYoutubePlayModal = ({
    selectedContent,
  }: {
    selectedContent: YoutubeContentApiResponse;
  }) => {
    setIsShowYotubePlayModal(false);

    if (selectedContent.affiliate) {
      setIsShowAffiliateModal(true);
    } else {
      // NOTE: コンテンツに紐づくアフィリエイトがない場合は、service 系のアフィリエイトを1日1回表示する
      const today = new Date().toISOString().split("T")[0];
      const lastViewDate = localStorage.getItem("lastViewDate");

      if (lastViewDate !== today) {
        setIsShowAffiliateModal(true);
        localStorage.setItem("lastViewDate", today);
      }
    }
  };

  return (
    <>
      {categories
        .sort((a, b) => (a.orderIndex || Infinity) - (b.orderIndex || Infinity))
        .map((category) => (
          <div key={category.id} id={category.id} ref={refs[category.id]}>
            <div>
              <H2Title
                id={category.id}
                style={{ backgroundColor: category.bgColor }}
                $isScrolling={isScrolling}
              >
                <img src={category.icon} alt={category.categoryName} width={40} className="me-3" />
                {category.categoryName}
              </H2Title>
              {subCategories
                .filter(({ categoryId }) => categoryId === category.id)
                .sort((a, b) => (a.orderIndex || Infinity) - (b.orderIndex || Infinity))
                .map((subCategory) => {
                  const subCategoryBorder = {
                    borderBottom: `5px solid ${subCategory.bgColor || category.bgColor}`,
                  };

                  return (
                    <div key={subCategory.id}>
                      <H3Title id={subCategory.id} style={subCategoryBorder}>
                        <img
                          src={subCategory.icon}
                          alt={subCategory.subCategoryName}
                          width={35}
                          className="me-2"
                        />
                        {subCategory.subCategoryName}
                      </H3Title>
                      <Container $isTwoColumn={isTwoColumn}>
                        {youtubeContents
                          .filter(({ categoryId }) => categoryId === category.id)
                          .filter(({ subCategoryId }) => subCategoryId === subCategory.id)
                          .sort((a, b) => (a.orderIndex || Infinity) - (b.orderIndex || Infinity))
                          .map((content) => (
                            <Block
                              key={category.id + content.id}
                              className={isDesktop ? "p-5" : "p-4"}
                            >
                              <Content
                                onClick={() => {
                                  handleItemClick(content);
                                  setIsShowYotubePlayModal(true);
                                }}
                              >
                                <div>
                                  <img
                                    style={{ width: "100%" }}
                                    src={content.thumbnail ?? "/noImage.jpg"}
                                  />
                                </div>
                                <H4Title>{content.contentName}</H4Title>
                                <SummaryBlockStyle>{content.summary}</SummaryBlockStyle>
                              </Content>
                            </Block>
                          ))}
                      </Container>
                    </div>
                  );
                })}
            </div>
            <AdComponent />
          </div>
        ))}
      {selectedContent && (
        <StyledModal
          $isDesktop={isDesktop}
          $isMobile={isMobile}
          show={isShowYotubePlayModal}
          onHide={() => closeYoutubePlayModal({ selectedContent })}
          scrollable={true}
        >
          <YouTubePlayModal content={selectedContent} />
        </StyledModal>
      )}
      {selectedContent && isShowAffiliateModal && (
        <StyledModal
          $isDesktop={isDesktop}
          $isMobile={isMobile}
          show={isShowAffiliateModal}
          onHide={() => setIsShowAffiliateModal(false)}
        >
          <AffiliateModal content={selectedContent} affiliates={affiliates} />
        </StyledModal>
      )}
    </>
  );
};

export default MainContent;
